import { AsyncPipe, NgClass, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  Input,
  Output
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, map } from 'rxjs';
import { DropDownItem } from '../../../models/shared/stylesheet/drop-down-item';
import { FormGroup } from '@angular/forms';
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { TableColumnHeaderSearchType } from '../../../models/enum/shared/table-column-header-search-type';
import { TableHeaderDateInputComponent } from '../table-column-inputs/table-header-date-input/table-header-date-input.component';
import { TableHeaderDropdownInputComponent } from '../table-column-inputs/table-header-dropdown-input/table-header-dropdown-input.component';
import { TableHeaderNumberInputComponent } from '../table-column-inputs/table-header-number-input/table-header-number-input.component';
import { TableHeaderTextInputComponent } from '../table-column-inputs/table-header-text-input/table-header-text-input.component';

@Component({
  selector: 'app-table-column-header',
  templateUrl: './table-column-header.component.html',
  styleUrls: ['./table-column-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [
    NgIf,
    NgClass,
    NgSwitch,
    NgSwitchCase,
    AsyncPipe,
    ReactiveFormsModule,
    NgbCollapse,
    TableHeaderTextInputComponent,
    TableHeaderDateInputComponent,
    TableHeaderDropdownInputComponent,
    TableHeaderNumberInputComponent
  ]
})
export class TableColumnHeaderComponent implements OnChanges {
  @Input() odataSortProperty: string = '';
  @Input() columnName: string = '';
  @Input() sortString: string = '';
  @Input() controlId: string = '';
  @Input() dropdownItems: DropDownItem[] = [];
  @Input() showSearchBar: boolean = false;
  @Input() searchType: TableColumnHeaderSearchType = TableColumnHeaderSearchType.Text;
  @Input() formGroup!: FormGroup;
  @Input() disableSort: boolean = false;
  @Output() updateActiveSort: EventEmitter<string> = new EventEmitter();

  private _activeSort = new BehaviorSubject<string>('');
  public readonly activeSort$ = this._activeSort as Observable<string>;

  private _dropdownItems = new BehaviorSubject<DropDownItem[]>([]);
  public readonly dropdownItems$ = this._dropdownItems as Observable<DropDownItem[]>;

  private _showSearchBar = new BehaviorSubject<boolean>(false);
  public readonly showSearchBar$ = this._showSearchBar as Observable<boolean>;

  public isAscSort$ = this.activeSort$.pipe(map(sort => sort === `${this.odataSortProperty} asc`));
  public isDescSort$ = this.activeSort$.pipe(map(sort => sort === `${this.odataSortProperty} desc`));

  public sortIconSrc$ = combineLatest([this.isAscSort$, this.isDescSort$]).pipe(
    map(([isAsc, isDesc]) => {
      if (isAsc) {
        return 'assets/icons/dark-grey/Arrow-Up.svg';
      } else if (isDesc) {
        return 'assets/icons/dark-grey/Arrow-Down.svg';
      }
      return null;
    })
  );

  public toggleSort(): void {
    combineLatest([this.isAscSort$, this.isDescSort$]).once(([isAsc, isDesc]) => {
      if (this.disableSort) return;
      if (isDesc) {
        this.updateActiveSort.emit('');
      } else if (isAsc) {
        const newSort = `${this.odataSortProperty} desc`;
        this.updateActiveSort.emit(newSort);
      } else {
        const newSort = `${this.odataSortProperty} asc`;
        this.updateActiveSort.emit(newSort);
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.sortString) {
      this._activeSort.next(this.sortString);
    }
    if (changes.dropdownItems) {
      this._dropdownItems.next(this.dropdownItems);
    }
    if (changes.showSearchBar) {
      this._showSearchBar.next(this.showSearchBar);
    }
  }

  protected readonly TableColumnHeaderSearchType = TableColumnHeaderSearchType;
}
